import moment from 'moment';
import { useCallback, useMemo, useState } from 'react';
import { useEnv } from '../../../../../../context/env-context';
import { useAuth } from '../../../../../../hooks/useAuth';
import Badge from 'react-bootstrap/Badge';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import HTMLEditor from '../../../../../../components/HTMLEditor';
import VirtualizedList from '../../../../../../components/VirtualizedList';
import { NumericFormat } from 'react-number-format';
import PopupMessageModal from './modals/PopupMessageModal';
import { toast } from 'react-toastify';
import { makeRequest } from '../../../../../../utils/makeRequest';
import { getHTMLBody, getHTMLWrapper } from '../../../../../../utils/strings';
import { MOBILE_DEVICE_DIMENSIONS } from '../../../../../../utils/mobileDevices';
import HTMLPreview from '../../../../../../components/HTMLPreview';

function PopupMessage({
  aggregators,
  popupMessage,
  setPopupMessage,
  popupRequest,
  setPopupList,
  width,
  setTabSpinner,
}) {
  const { apiOriginAggregator } = useEnv();
  const { getAccessTokenSilently } = useAuth();

  const [popupName, setPopupName] = useState(popupMessage.name);
  const [popupMaxShowCount, setPopupMaxShowCount] = useState(popupMessage.maxShowCount || null);

  const [popupMessageModal, setPopupMessageModal] = useState(null);

  const [preview, setPreview] = useState({
    device: 'small_phone',
    width:
      MOBILE_DEVICE_DIMENSIONS.small_phone.viewport_width *
      MOBILE_DEVICE_DIMENSIONS.small_phone.scale,
    height:
      MOBILE_DEVICE_DIMENSIONS.small_phone.viewport_height *
      MOBILE_DEVICE_DIMENSIONS.small_phone.scale,
  });
  const [language, setLanguage] = useState('en');
  const [htmlBody, setHtmBody] = useState({
    en: getHTMLBody(popupMessage.html.en),
    es: getHTMLBody(popupMessage.html.es),
  });

  const savePopupMessage = useCallback(async () => {
    setTabSpinner(true);

    const token = await getAccessTokenSilently();

    if (!token) {
      return;
    }

    const config = {
      token,
      url: `${apiOriginAggregator}/popup/save`,
      method: 'POST',
      data: JSON.stringify({
        ...popupMessage,
        name: popupName,
        maxShowCount: popupMaxShowCount,
        html: { en: getHTMLWrapper(htmlBody.en), es: getHTMLWrapper(htmlBody.es) },
      }),
    };

    try {
      await makeRequest(config);
      await popupRequest({ url: 'list', onSuccess: response => setPopupList(response) });

      setPopupMessage(null);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setTabSpinner(false);
    }
  }, [
    apiOriginAggregator,
    getAccessTokenSilently,
    setTabSpinner,
    popupMessage,
    popupName,
    popupMaxShowCount,
    htmlBody,
    popupRequest,
    setPopupList,
    setPopupMessage,
  ]);

  const tableColumns = useMemo(
    () => [
      {
        key: 'start',
        header: 'From',
        width: 150,
      },
      {
        key: 'end',
        header: 'To',
        width: 150,
      },
      {
        key: 'isActive',
        header: '',
        width: 80,
        renderValue: value =>
          value ? (
            <Badge className="bo-inline-badge" bg="success">
              Active
            </Badge>
          ) : null,
      },
      {
        key: 'aggregatorIds',
        header: '',
        width: 300,
        renderValue: value =>
          value.length > 0 ? (
            <div>
              {value.map(x => (
                <div key={x}>
                  {(aggregators.length > 0 && aggregators.find(agg => agg.id === x)?.name) || '---'}
                </div>
              ))}
            </div>
          ) : (
            'All Aggregators'
          ),
      },
      {
        key: 'edit',
        header: '',
        width: 50,
        renderValue: (_, row) => (
          <Button
            className="bo-common-inline-button"
            onClick={() => {
              setPopupMessageModal({
                startDate: moment(row.start).format('YYYY-MM-DD'),
                endDate: moment(row.end).format('YYYY-MM-DD'),
                aggregatorIds: row.aggregatorIds,
                tempId: row.tempId,
              });
            }}
          >
            Edit
          </Button>
        ),
      },
      {
        key: 'delete',
        header: '',
        width: 70,
        renderValue: (_, row) => (
          <Button
            className="bo-common-inline-button"
            onClick={() => {
              setPopupMessage(prevMessage => ({
                ...prevMessage,
                activationPeriods: prevMessage.activationPeriods.filter(
                  x => x.tempId !== row.tempId,
                ),
              }));
            }}
          >
            Delete
          </Button>
        ),
      },
    ],
    [aggregators, setPopupMessage],
  );

  const getRowHeight = useCallback(
    index =>
      Math.max((popupMessage?.activationPeriods[index]?.aggregatorIds?.length || 0) * 18 + 18, 36),
    [popupMessage],
  );

  const listHeight = useMemo(
    () => popupMessage.activationPeriods.reduce((acc, _, idx) => acc + getRowHeight(idx), 0) + 36,
    [popupMessage, getRowHeight],
  );

  const closePopupMessageModal = () => setPopupMessageModal(null);

  return (
    <>
      <div className="d-flex flex-column align-items-start">
        <Button
          className="bo-button-inline-link px-0 fw-bold mb-2"
          onClick={() => setPopupMessage(null)}
        >
          {'< Back to Referral Campaigns List'}
        </Button>
        <div className="w-100 d-flex justify-content-between align-items-end gap-2 mb-3">
          <div className="d-flex gap-2">
            <div>
              <Form.Label className="mb-1">Name</Form.Label>
              <Form.Control
                className="bo-input bo-w-400"
                type="text"
                value={popupName}
                onChange={e => setPopupName(e.target.value)}
              />
            </div>
            <div>
              <Form.Label className="mb-1">Max Show Count</Form.Label>
              <NumericFormat
                className="bo-input bo-w-150"
                value={popupMaxShowCount}
                customInput={Form.Control}
                onValueChange={({ floatValue }) => setPopupMaxShowCount(floatValue ?? null)}
                allowNegative={false}
                decimalScale={0}
              />
            </div>
          </div>
          <Button
            className="bo-button bo-w-120"
            disabled={!popupName || popupMaxShowCount === null}
            onClick={savePopupMessage}
          >
            Save
          </Button>
        </div>
        <div className="border mb-3">
          <VirtualizedList
            tableRows={popupMessage.activationPeriods}
            tableColumns={tableColumns}
            width={width}
            virtualization={false}
            height={listHeight}
            getRowHeight={getRowHeight}
            extendColumnKey="aggregatorIds"
            rowKey="tempId"
          />
          <Button
            className="bo-common-inline-button mx-3 my-2"
            onClick={() =>
              setPopupMessageModal({
                startDate: moment().format('YYYY-MM-DD'),
                endDate: moment().add(1, 'months').format('YYYY-MM-DD'),
                aggregatorIds: [],
                tempId: 'new',
              })
            }
          >
            New Period
          </Button>
        </div>
        <div className="d-flex">
          <HTMLEditor
            width={width}
            preview={preview}
            language={language}
            setLanguage={setLanguage}
            value={htmlBody[language]}
            onEditorChange={newValue => setHtmBody(prev => ({ ...prev, [language]: newValue }))}
            onLoadContent={() => setTabSpinner(false)}
            allowCustomButtons
          />
          <HTMLPreview
            value={htmlBody[language]}
            preview={preview}
            setPreview={setPreview}
            type="popup"
          />
        </div>
      </div>
      <PopupMessageModal
        aggregators={aggregators}
        popupMessageModal={popupMessageModal}
        closeModal={closePopupMessageModal}
        setPopupMessage={setPopupMessage}
      />
    </>
  );
}

export default PopupMessage;
